import { CacheStore } from "@/modules/cache/cache-store";
import { GLOBAL_CACHE_DB_IDENTIFIER } from "@/modules/cache/constants";
import { cacheClientManager } from "@/manager/cache-client";
import { CacheClient } from "@/manager/cache-client/CacheClient";
import { getApiAuthIdentifier } from "@/modules/api/authUtils";
import { CacheKey } from "@/modules/cache/constants";
import { resolveCacheScopeId } from "@/modules/cache/scope";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

type GlobalCacheGet = ReturnType<CacheClient["get"]>;
type GlobalCacheSet = ReturnType<CacheClient["set"]>;
type GlobalCacheDel = ReturnType<CacheClient["del"]>;

type GlobalCache = {
  get: GlobalCacheGet;
  set: GlobalCacheSet;
  del: GlobalCacheDel;
  clearAll: CacheClient["clearAll"];
};

const resolveScopedCacheClient = async <TCacheKey extends CacheKey>(cacheKey: TCacheKey) => {
  const scopeId = await resolveCacheScopeId({
    cacheKey,
    resolveDefaultScopeId: getApiAuthIdentifier,
  });

  if (!scopeId) {
    return null;
  }

  const cacheClient = cacheClientManager.instance();

  return {
    cacheClient,
    scopeId,
  };
};

const getCache: GlobalCacheGet = async (cacheKey, options) => {
  const resolved = await resolveScopedCacheClient(cacheKey);
  if (!resolved) {
    logger.warn({ message: "[GLOBAL_CACHE.get] No scopeId found", info: { cacheKey } });
    return null;
  }

  const { cacheClient, scopeId } = resolved;
  return cacheClient.get(scopeId)(cacheKey, options);
};

const setCache: GlobalCacheSet = async (cacheKey, payload) => {
  const resolved = await resolveScopedCacheClient(cacheKey);

  if (!resolved) {
    logger.warn({
      message: "[GLOBAL_CACHE.set] No scopeId found",
      info: { cacheKey, payload: objectModule.summarizeForLogging(payload) },
    });
    return false;
  }

  const { cacheClient, scopeId } = resolved;
  return cacheClient.set(scopeId)(cacheKey, payload);
};

const deleteCache: GlobalCacheDel = async cacheKey => {
  const resolved = await resolveScopedCacheClient(cacheKey);

  if (!resolved) {
    logger.warn({ message: "[GLOBAL_CACHE.del] No scopeId found", info: { cacheKey } });
    return false;
  }

  const { cacheClient, scopeId } = resolved;
  return cacheClient.del(scopeId)(cacheKey);
};

const clearAllCache: CacheClient["clearAll"] = async () => {
  return cacheClientManager.instance().clearAll();
};

/**
 * @deprecated Use the store.globalCache instead
 */
export const LEGACY_GLOBAL_CACHE_INSTANCE = {
  get: getCache,
  set: setCache,
  del: deleteCache,
  clearAll: clearAllCache,
} as GlobalCache;

/**
 * @deprecated Prefer the store.globalCache or store.accountCache instead.
 *
 * This instance exposes an instance which matches our store.globalCache for backward compatibility...
 * Ideally we can access everything through the store/react-context instead.
 */
export const GLOBAL_CACHE_INSTANCE = new CacheStore({
  databaseId: GLOBAL_CACHE_DB_IDENTIFIER,
});
